import { Link, useLocation, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { useVersion } from '../hooks/useVersion'
import { useJobsStatus } from '../api/jobs'
import { useLiveSubscription, useScopeLive } from '../contexts/LiveEventsContext'
import { useCan, useMemberKind, usePermissions } from '../contexts/PermissionsContext'
import { useKnowledgeProposalCount } from '../hooks/useKnowledge'
import { entryVisible, shellForKind, visibleMenu, type SidebarEntry } from '../lib/sidebar-menus'
import { useFeatureFlag } from '../hooks/use-feature-flags'
import { useMyOrganizations } from '../api/multiOrg'
import { mdiAlertOctagon, mdiCheckCircle, mdiChevronDown, mdiSync } from '@mdi/js'
import { useState } from 'react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import { LanguageToggle } from './LanguageToggle'
import { ThemeToggle } from './ThemeToggle'
import { OrgLogo } from './OrgLogo'
import { Logomark } from './Logomark'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from './ui/sidebar'
import { FeedbackButton } from './feedback/FeedbackButton'
import { useFeedbackContext } from './feedback/FeedbackContext'
import { AppIcon } from './ui/icon'
// One icon table for the whole app: the sidebar and every PageHeader read the
// same key, so a page's header icon is this entry by construction.
import { SECTION_ICONS } from '../lib/section-icons'
import { GroupSidebar, isGroupSpacePath } from './group-steering/GroupSidebar'
import { AttentionTitle, NavAttention, TimeZoneSync, useCollapsedAttentionLabel } from './NavAttention'
import { useAttentionCounts } from '../hooks/useAttentionCounts'
import { isAttentionEntry, type AttentionCounts } from '../lib/attention'

// The bug-report trigger for phones. The header instance is `hidden md:flex`
// and ⌘/Ctrl+Shift+B needs a keyboard, so without this row there is no way at
// all to open the feedback dialog on a phone (#1181). Opening it must also
// close the sidebar Sheet — otherwise the sheet renders over the dialog.
function MobileFeedbackButton() {
  const { setOpenMobile } = useSidebar()
  const { open: openFeedback, isCapturing, isRecording } = useFeedbackContext()

  return (
    <FeedbackButton
      testId="feedback-trigger-btn-mobile"
      isCapturing={isCapturing}
      isRecording={isRecording}
      onClick={() => {
        setOpenMobile(false)
        // Let the Sheet finish closing before useFeedback() screenshots the
        // page, otherwise the report captures the sidebar overlay instead of
        // whatever the user was actually looking at.
        setTimeout(openFeedback, 250)
      }}
    />
  )
}

// Renders only the button itself — NOT wrapped in its own `SidebarMenuItem` —
// because it shares one footer row with `FooterVersion` (spec
// 2026-09-12-03): jobs on the left, the build version right-aligned via
// `ml-auto` on the sibling. The single `SidebarMenuItem` for that row is
// supplied by the caller (`AppSidebar`'s `SidebarFooter`), matching every
// other footer entry.
function JobsStatusIndicator({ org }: { org: string }) {
  // The sidebar is mounted org-wide (every page), so this is the steady-state
  // "monitor all jobs" subscription — distinct from the jobs LIST page's own
  // useLiveSubscription({ entity: 'jobs' }) call, but refcounted against it
  // in LiveEventsContext's registry (both resolve to the same wire
  // subscription; only the last one to unmount actually unsubscribes).
  useLiveSubscription({ entity: 'jobs' })
  const isJobsScopeLive = useScopeLive({ entity: 'jobs' })
  const { data } = useJobsStatus(org, isJobsScopeLive)
  const total = data?.total_count ?? 0
  const done = data?.done_count ?? 0
  const blocking = data?.blocking_count ?? 0
  // The blocked state must survive an EMPTY queue — that is exactly the
  // pathology (spec 2026-08-18-01): the jobs all ran, deferred, and drained,
  // leaving nothing pending and no failure while hundreds of documents sit
  // flagged. So the total_count === 0 early-out only applies when nothing
  // blocks.
  if (!data || (total === 0 && blocking === 0)) return null
  const allDone = done >= total
  // Blocked takes precedence over both done and running: blocked beats
  // spinning, because the work will not finish on its own.
  if (blocking > 0) {
    return (
      <SidebarMenuButton asChild className="w-auto">
        <Link to="/$org/jobs/blockers" params={{ org }} data-testid="jobs-blocked-indicator">
          <AppIcon path={mdiAlertOctagon} size="sm" className="text-destructive" />
          <span className="tabular-nums">{blocking} · {done}/{total}</span>
        </Link>
      </SidebarMenuButton>
    )
  }
  return (
    <SidebarMenuButton asChild className="w-auto">
      <Link to="/$org/jobs" params={{ org }} search={{ type: undefined, status: undefined, limit: 30, offset: 0, hide_noop: true }}>
        {allDone ? (
          <AppIcon path={mdiCheckCircle} size="sm" className="text-success" />
        ) : (
          <AppIcon
            path={mdiSync}
            size="sm"
            className="text-muted-foreground motion-safe:animate-spin"
          />
        )}
        <span className="tabular-nums">{done}/{total}</span>
      </Link>
    </SidebarMenuButton>
  )
}

/**
 * The build version + (on a mismatch) reload button, right-aligned on the
 * footer's jobs row.
 *
 * Deliberately gate-free: `useVersion` fetches unconditionally on every
 * route, including org-less ones (`/organizations`, `/no-organization`), so
 * this renders wherever `versionInfo?.buildVersion` is set — it must NOT
 * inherit `JobsStatusIndicator`'s org/membership/`data.read` gates, which
 * exist only because that component subscribes to live events and polls an
 * org-scoped endpoint (spec 2026-09-06-04). A version string does neither.
 */
function FooterVersion({ versionInfo, hasVersionMismatch }: { versionInfo: { buildVersion: string }; hasVersionMismatch: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="ml-auto flex shrink-0 items-center gap-1">
      <span
        data-testid="sidebar-app-version"
        title={versionInfo.buildVersion}
        className={`text-[10px] ${hasVersionMismatch ? 'text-destructive/70' : 'text-muted-foreground/40'}`}
      >
        {versionInfo.buildVersion}
      </span>
      {hasVersionMismatch && (
        <button
          onClick={() => window.location.reload()}
          title={t('app.new_version')}
          data-testid="version-reload-btn"
          className="inline-flex items-center justify-center w-6 h-6 shrink-0 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors motion-safe:[&:hover_svg]:animate-spin"
        >
          <AppIcon path={SECTION_ICONS.refresh} size="sm" />
        </button>
      )}
    </div>
  )
}

/**
 * SidebarEntryItem draws one menu entry. Which entries show, in which order
 * and under which permission is decided in lib/sidebar-menus.ts; this only
 * knows each destination's link and label.
 */
function SidebarEntryItem({ entry, org, attention }: { entry: SidebarEntry; org: string; attention?: AttentionCounts }) {
  const { t } = useTranslation()
  const { t: tConversations } = useTranslation('conversations')
  const { t: tSignatures } = useTranslation('signatures')
  const { t: tKnowledge } = useTranslation('knowledge')
  const { t: tCampaigns } = useTranslation('campaigns')
  const { t: tBenchmark } = useTranslation('benchmark')
  const { t: tAssemblies } = useTranslation('assemblies')
  const { t: tLeases } = useTranslation('leases')
  const { t: tListings } = useTranslation('listings')
  const { t: tApplications } = useTranslation('rentalApplications')
  const label = (fallback: string) => (entry.labelKey ? t(entry.labelKey) : fallback)
  // The red badges of spec 2026-10-27-23, on the staff menu only.
  const attentionEntry = attention && isAttentionEntry(entry.id) ? entry.id : null
  const collapsedLabel = useCollapsedAttentionLabel(attentionEntry, attention)

  const link = (() => {
    switch (entry.id) {
      case 'home':
        return (
          <Link to="/$org" params={{ org }} activeOptions={{ exact: true }} data-testid="nav-home">
            <AppIcon path={SECTION_ICONS.home} size="md" />
            {label(t('nav.home'))}
          </Link>
        )
      case 'search':
        return (
          <Link
            to="/$org/search"
            params={{ org }}
            search={{ q: '', entity_types: undefined, tags: undefined, scope: 'properties' }}
            data-testid="nav-search"
          >
            <AppIcon path={SECTION_ICONS.search} size="md" />
            {label(t('nav.search'))}
          </Link>
        )
      case 'conversations':
        return (
          <Link
            to="/$org/conversations"
            params={{ org }}
            search={{
              status: undefined,
              channel: undefined,
              resolution: undefined,
              asset: undefined,
              unattached: undefined,
              draft: undefined,
              q: undefined,
              conversation: undefined,
              message: undefined,
              selected: undefined,
              reply_for_issue: undefined,
            }}
            data-testid="nav-conversations"
          >
            <AppIcon path={SECTION_ICONS.conversations} size="md" />
            {label(tConversations('nav'))}
          </Link>
        )
      case 'assets':
        return (
          <Link
            to="/$org/assets"
            params={{ org }}
            search={{ q: undefined, flag: undefined, asset_type_code: undefined, asset_type_uid: undefined, showBuildingGroups: undefined, showBuildings: undefined, archived: undefined }}
            data-testid="nav-assets"
          >
            <AppIcon path={SECTION_ICONS.assets} size="md" />
            {label(t('nav.assets'))}
          </Link>
        )
      case 'contracts':
        return (
          <Link to="/$org/contracts" params={{ org }} search={{}} data-testid="nav-contracts">
            <AppIcon path={SECTION_ICONS.contracts} size="md" />
            {label(t('nav.contracts'))}
          </Link>
        )
      case 'leases':
        return (
          <Link
            to="/$org/leases"
            params={{ org }}
            search={{ under: undefined, regime: undefined, ends_before: undefined, status: undefined, page: undefined }}
            data-testid="nav-leases"
          >
            <AppIcon path={SECTION_ICONS.leases} size="md" />
            {label(tLeases('nav'))}
          </Link>
        )
      case 'listings':
        return (
          <Link to="/$org/listings" params={{ org }} search={{ status: undefined, asset: undefined }} data-testid="nav-listings">
            <AppIcon path={SECTION_ICONS.listings} size="md" />
            {label(tListings('nav'))}
          </Link>
        )
      case 'rental-applications':
        return (
          <Link to="/$org/rental-applications" params={{ org }} search={{ listing: undefined }} data-testid="nav-rental-applications">
            <AppIcon path={SECTION_ICONS['rental-applications']} size="md" />
            {label(tApplications('nav'))}
          </Link>
        )
      case 'assemblies':
        return (
          <Link to="/$org/assemblies" params={{ org }} search={{ asset: undefined }} data-testid="nav-assemblies">
            <AppIcon path={SECTION_ICONS.assemblies} size="md" />
            {label(tAssemblies('nav'))}
          </Link>
        )
      case 'signatures':
        return (
          <Link to="/$org/signatures" params={{ org }} search={{ status: undefined }} data-testid="nav-signatures">
            <AppIcon path={SECTION_ICONS.signatures} size="md" />
            {label(tSignatures('nav'))}
          </Link>
        )
      case 'knowledge':
        return (
          <Link to="/$org/knowledge" params={{ org }} search={{ q: undefined, kind: undefined }} data-testid="nav-knowledge">
            <AppIcon path={SECTION_ICONS.knowledge} size="md" />
            {label(tKnowledge('nav'))}
          </Link>
        )
      case 'campaigns':
        return (
          <Link to="/$org/campaigns" params={{ org }} data-testid="nav-campaigns">
            <AppIcon path={SECTION_ICONS.campaigns} size="md" />
            {label(tCampaigns('nav'))}
          </Link>
        )
      case 'users':
        return (
          <Link to="/$org/users" params={{ org }} search={{}} data-testid="nav-users">
            <AppIcon path={SECTION_ICONS.users} size="md" />
            {label(t('nav.users'))}
          </Link>
        )
      case 'capex':
        return (
          <Link
            to="/$org/capex-plans"
            params={{ org }}
            search={{ workflow_status: undefined, archived: undefined }}
            data-testid="nav-capex"
          >
            <AppIcon path={SECTION_ICONS.capex} size="md" />
            {label(t('nav.capex'))}
          </Link>
        )
      case 'steering':
        return (
          <Link
            to="/$org/steering"
            params={{ org }}
            search={{ category: undefined, view: undefined, selected: undefined, subcategory: undefined, asset: undefined, subtree: undefined, due: undefined, status: undefined, sort: undefined, q: undefined, page: undefined, tab: undefined, nature: undefined }}
            data-testid="nav-steering"
          >
            <AppIcon path={SECTION_ICONS.steering} size="md" />
            {label(t('nav.steering'))}
          </Link>
        )
      case 'group_steering':
        return (
          <Link
            to="/$org/group"
            params={{ org }}
            search={{ period: undefined, level: undefined, family: undefined, kpi: undefined, selected: undefined, q: undefined }}
            data-testid="nav-group-steering"
          >
            <AppIcon path={SECTION_ICONS.group_steering} size="md" />
            {label(t('nav.group_steering'))}
          </Link>
        )
      case 'benchmark':
        return (
          <Link
            to="/$org/benchmark"
            params={{ org }}
            search={{ view: undefined, metric: undefined, key: undefined, asset: undefined, window_end: undefined, strata: undefined, currency: undefined }}
            data-testid="nav-benchmark"
          >
            <AppIcon path={SECTION_ICONS.benchmark} size="md" />
            {label(tBenchmark('nav'))}
          </Link>
        )
      case 'documents':
        return (
          <Link
            to="/$org/documents"
            params={{ org }}
            search={{ asset_uid: undefined, document_type_uid: undefined, flags: undefined, limit: 20 }}
            data-testid="nav-documents"
          >
            <AppIcon path={SECTION_ICONS.documents} size="md" />
            {label(t('nav.documents'))}
          </Link>
        )
      case 'expenses':
        return (
          <Link to="/$org/expenses" params={{ org }} data-testid="nav-expenses">
            <AppIcon path={SECTION_ICONS.expenses} size="md" />
            {label(t('nav.expenses'))}
          </Link>
        )
      case 'interventions':
        return (
          <Link
            to="/$org/interventions"
            params={{ org }}
            search={{ kind: undefined, status: undefined }}
            data-testid="nav-interventions"
          >
            <AppIcon path={SECTION_ICONS.interventions} size="md" />
            {label(t('nav.interventions'))}
          </Link>
        )
      case 'issues':
        return (
          <Link to="/$org/issues" params={{ org }} data-testid="nav-issues">
            <AppIcon path={SECTION_ICONS.requests} size="md" />
            {label(t('nav.my_requests'))}
          </Link>
        )
      case 'imports':
        return (
          <Link to="/$org/imports" params={{ org }} data-testid="nav-imports">
            <AppIcon path={SECTION_ICONS.imports} size="md" />
            {label(t('nav.imports'))}
          </Link>
        )
      case 'agents':
        return (
          <Link to="/$org/agent-runs" params={{ org }} search={{}} data-testid="nav-agents">
            <AppIcon path={SECTION_ICONS.agentRun} size="md" />
            {label(t('nav.agents'))}
          </Link>
        )
      case 'activity':
        return (
          <Link
            to="/$org/activity"
            params={{ org }}
            search={{ from: undefined, to: undefined, entity_table: undefined, user_uid: undefined, operation: undefined }}
            data-testid="nav-activity"
          >
            <AppIcon path={SECTION_ICONS.activity} size="md" />
            {label(t('nav.activity'))}
          </Link>
        )
    }
  })()

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild aria-label={collapsedLabel}>{link}</SidebarMenuButton>
      {attentionEntry && <NavAttention entry={attentionEntry} counts={attention} org={org} />}
      {entry.id === 'knowledge' && <KnowledgeProposalsBadge org={org} />}
    </SidebarMenuItem>
  )
}

/** The pending knowledge proposals the caller may decide (spec 2026-11-01-01). */
function KnowledgeProposalsBadge({ org }: { org: string }) {
  const canWrite = useCan('knowledge.write')
  const { data: count } = useKnowledgeProposalCount(org, canWrite)
  if (!count) return null

  return <SidebarMenuBadge data-testid="nav-knowledge-proposals-count">{count}</SidebarMenuBadge>
}

const COLLAPSED_GROUPS_KEY = 'sidebar.collapsed-groups'

function readCollapsedGroups(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(COLLAPSED_GROUPS_KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

function writeCollapsedGroups(ids: string[]) {
  try {
    localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify(ids))
  } catch {
    // Private window or blocked storage: the group just opens expanded next time.
  }
}

/**
 * SidebarMenuGroup draws one group of the menu. A labelled group (its entries
 * carry `group`) is a collapsible with a toggle label; its collapsed state is
 * kept per browser. It renders open whenever the current route is one of its
 * entries, and as plain icons when the whole sidebar is collapsed to the rail.
 */
function SidebarMenuGroup({ group, org, attention }: { group: SidebarEntry[]; org: string; attention?: AttentionCounts }) {
  const { t } = useTranslation()
  const { state } = useSidebar()
  const location = useLocation()
  const groupId = group[0]?.group
  const [collapsedIds, setCollapsedIds] = useState(readCollapsedGroups)

  const items = (
    <SidebarMenu>
      {group.map((entry) => (
        <SidebarEntryItem key={entry.id} entry={entry} org={org} attention={attention} />
      ))}
    </SidebarMenu>
  )

  if (!groupId || state === 'collapsed') {
    return (
      <SidebarGroup>
        <SidebarGroupContent>{items}</SidebarGroupContent>
      </SidebarGroup>
    )
  }

  const hasActive = group.some((entry) => {
    const base = `/${org}/${entry.id}`
    return location.pathname === base || location.pathname.startsWith(`${base}/`)
  })
  const open = hasActive || !collapsedIds.includes(groupId)
  const toggle = () => {
    const next = open ? [...collapsedIds, groupId] : collapsedIds.filter((id) => id !== groupId)
    setCollapsedIds(next)
    writeCollapsedGroups(next)
  }

  return (
    <SidebarGroup data-testid={`sidebar-group-${groupId}`}>
      <Collapsible open={open}>
        <SidebarGroupLabel asChild>
          <CollapsibleTrigger
            onClick={toggle}
            aria-expanded={open}
            className="w-full justify-between cursor-pointer"
            data-testid={`sidebar-group-${groupId}-toggle`}
          >
            {t(`nav.${groupId}`)}
            <AppIcon path={mdiChevronDown} size="sm" className={open ? '' : '-rotate-90'} />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent>
          <SidebarGroupContent>{items}</SidebarGroupContent>
        </CollapsibleContent>
      </Collapsible>
    </SidebarGroup>
  )
}

export function AppSidebar() {
  const { t } = useTranslation()
  const { isAuthenticated, user, logout } = useAuth()
  const { versionInfo, hasVersionMismatch } = useVersion()
  const params = useParams({ strict: false })
  const location = useLocation()
  const org = params.org || 'demo'
  // Every entry declares the `read` permission its destination needs, taken
  // from the same table the route guard uses (lib/route-permissions.ts), so the
  // sidebar can never offer a link the guard would bounce — the two read the
  // same signal, so they agree by construction.
  //
  // An entry disappears only on a KNOWN denial: a membership summary that was
  // found for this org and does not carry the permission. Outside an org
  // (/organizations, /no-organization) there is no membership to read, and a
  // failed or still-unauthenticated `/users/me/organizations` leaves the menu
  // intact rather than presenting an empty shell — the API is the enforcer, the
  // sidebar only declutters.
  const { permissions, membershipKnown } = usePermissions()
  const inOrg = Boolean(params.org)
  // The kind picks the menu; an unknown kind (loading, outside an org) keeps
  // the staff one, so nothing disappears on an unknown answer.
  const kind = useMemberKind()
  const shell = shellForKind(inOrg ? kind : undefined)
  const permissionsKnown = inOrg && membershipKnown
  const lettings = useFeatureFlag<boolean>(params.org ?? '', 'lettings', false)
  const menuGroups = visibleMenu(inOrg ? kind : undefined, permissions, permissionsKnown, lettings)
  const showOrgSettings = shell.orgSettings &&
    entryVisible({ id: 'activity', permission: 'organization.read' }, permissions, permissionsKnown)

  // The sidebar brand: the current org's logo + name, read from the
  // membership summary the shell already fetches for the permission set and
  // the org switcher (no extra request — see PermissionsProvider, which
  // reads the very same `useMyOrganizations()` query). Falls back to the
  // product wordmark whenever there is no membership to show: outside an org
  // (/organizations, /no-organization), while the summary is still loading
  // or failed, or when the current org isn't one of the caller's
  // memberships — the header must never render empty or a stale name.
  const { data: myOrgsData, isError: myOrgsError } = useMyOrganizations()
  const myOrgsSettled = Boolean(myOrgsData) || myOrgsError
  const membership = inOrg ? myOrgsData?.data.find((o) => o.code === org) : undefined
  const showOrgBrand = inOrg && myOrgsSettled && Boolean(membership)

  // See the comment above JobsStatusIndicator's mount point in SidebarFooter:
  // these three gates exist for that component's live-subscription/poll, not
  // for the version string next to it.
  const showJobsIndicator = Boolean(params.org && membershipKnown && permissions.includes('data.read'))
  const showFooterVersion = Boolean(versionInfo?.buildVersion)

  // What waits on a manager (spec 2026-10-27-23): the staff menu's red
  // badges and the « (N) » of the tab title. Residents and providers keep
  // their menu as it is.
  const staffAttention = inOrg && kind === 'staff'
  const attention = useAttentionCounts(org, staffAttention)

  if (!isAuthenticated) {
    return null
  }

  // The group steering console is a space of its own (spec 2026-09-11-01):
  // same shell, same provider, another sidebar. The main menu links to it
  // for whoever holds group_steering.read.
  if (inOrg && isGroupSpacePath(location.pathname, org)) {
    return <GroupSidebar org={org} />
  }

  return (
    <Sidebar>
      {staffAttention && <AttentionTitle total={attention?.total ?? 0} />}
      {staffAttention && <TimeZoneSync org={org} />}
      <SidebarHeader>
        <div className="px-2 py-2 flex items-center justify-between gap-2">
          {/* The brand, NOT a heading. It used to be a hardcoded wordmark
              <span>, itself a fix for an even older <h1> that made it the
              FIRST h1 of every authenticated document — ahead of the routed
              page's own <h1>, the one that names what is on screen. Beyond
              the a11y outline, that first-in-DOM h1 silently defeated
              `page.locator('h1').first()` readiness gates (they resolved on
              the shell, before the routed page had rendered) and
              `document.querySelector('h1')` fallbacks: a folder drop meant
              for the documents page landed on the sidebar and was discarded.
              Keep it a plain element in both branches below. */}
          {showOrgBrand && membership ? (
            <Link
              to="/$org"
              params={{ org }}
              data-testid="sidebar-org-brand"
              className="flex items-center gap-1.5 min-w-0 flex-1 hover:opacity-80 transition-opacity"
            >
              <OrgLogo
                org={org}
                size={32}
                hasPicture={Boolean(membership.picture?.document_uid)}
                name={membership.name}
                data-testid="sidebar-org-logo"
              />
              <span
                data-testid="sidebar-org-name"
                title={membership.name}
                className="text-base font-semibold text-foreground tracking-tight truncate"
              >
                {membership.name}
              </span>
            </Link>
          ) : (
            <div className="flex items-baseline gap-1.5 min-w-0 flex-1" data-testid="sidebar-org-brand">
              <span className="text-base font-semibold text-foreground tracking-tight truncate">{t('app.name')}</span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* The menu is described per member kind in lib/sidebar-menus.ts
            (spec 2026-10-07-03): staff get the manager menu, residents their
            own short list. This only draws the entries it is handed. */}
        {menuGroups.map((group, index) => (
          <SidebarMenuGroup key={group[0]?.group ?? index} group={group} org={org} attention={staffAttention ? attention : undefined} />
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <div
            className="md:hidden flex items-center gap-1 px-2 py-1"
            data-testid="sidebar-mobile-toggles"
          >
            <MobileFeedbackButton />
            <LanguageToggle />
            <ThemeToggle />
          </div>
          {/* JobsStatusIndicator reads the jobs of one organization, so only
              render it when a real :org param is present (the sidebar also
              shows on /organizations, /no-organization and /admin). The live
              hooks are inert outside a LiveEventsProvider, so this gate is
              about which org to query, not about avoiding a throw. */}
          {/* And unlike a nav entry, this one POLLS: `can` answers optimistically
              while the membership is still unknown, which is right for
              decluttering the nav (no empty-shell flash) and wrong here — an
              optimistic mount fires GET /jobs/status, collects a 403 and one
              permission-denied notification per page load for every member who
              does not hold `data.read` (spec 2026-09-06-04). Optimism is for
              declutter, never for a network call: wait until the permission is
              actually held. */}
          {/* The build version shares this row but NOT these gates (spec
              2026-09-12-03) — see FooterVersion's docblock. When
              JobsStatusIndicator is absent (no org, unknown membership, no
              data.read, or simply nothing to report) the version still
              renders alone, right-aligned via its own `ml-auto`. */}
          {showJobsIndicator && (
            <SidebarMenuItem>
              <div className="flex w-full items-center gap-2 px-2 py-1" data-testid="sidebar-footer-status-row">
                <JobsStatusIndicator org={org} />
              </div>
            </SidebarMenuItem>
          )}
          {/* The product row (spec 2026-09-17-03). The footer used to say what
              product this is only through a git hash; the mark plus the name
              is the one place the shell names itself, and it is also what the
              build version now hangs off — `FooterVersion` carries its own
              `ml-auto`, so it right-aligns here exactly as it did on the jobs
              row it used to share. Unlike that row this one is ungated, which
              is what keeps the version on screen on org-less routes. */}
          <SidebarMenuItem>
            <div
              className="flex w-full items-center gap-2 px-2 py-1 text-xs text-muted-foreground"
              data-testid="sidebar-footer-product-row"
            >
              <Logomark size={16} />
              <span className="shrink-0 whitespace-nowrap">{t('app.name')}</span>
              {showFooterVersion && (
                <FooterVersion versionInfo={versionInfo!} hasVersionMismatch={hasVersionMismatch} />
              )}
            </div>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  data-testid="sidebar-user-menu"
                >
                  <AppIcon path={SECTION_ICONS.account} size="md" />
                  {user?.name || 'User'}
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="right" className="w-56">
                <DropdownMenuLabel>{user?.name || 'User'}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/$org/user/me/profile" params={{ org }} className="w-full flex items-center">
                    <AppIcon path={SECTION_ICONS.account} size="md" className="mr-2" />
                    {t('user_menu.user')}
                  </Link>
                </DropdownMenuItem>
                {showOrgSettings && (
                  <DropdownMenuItem asChild>
                    <Link to="/$org/settings" params={{ org }} className="w-full flex items-center">
                      <AppIcon path={SECTION_ICONS.settings} size="md" className="mr-2" />
                      {t('user_menu.organization')}
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout(org)} className="text-destructive focus:text-destructive" data-testid="sidebar-logout">
                  <AppIcon path={SECTION_ICONS.logout} size="md" className="mr-2" />
                  {t('actions.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
